import type { Matriz } from './tipos'

export type EstadoDeEtapa = 'completo' | 'en_curso' | 'pendiente'

export interface EtapaDeLaMatriz {
  ruta: 'alcance' | 'levantamiento' | 'matriz' | 'oficializar'
  numero: string
  titulo: string
  detalle: string
  /** Estado leído del progreso que entrega el servidor; nunca un avance estimado. */
  estado: (m: Matriz) => EstadoDeEtapa
}

export const ETAPAS_DE_LA_MATRIZ: EtapaDeLaMatriz[] = [
  { ruta: 'alcance', numero: '1', titulo: 'Alcance', detalle: 'Entidad, centro y participantes', estado: () => 'completo' },
  {
    ruta: 'levantamiento',
    numero: '2',
    titulo: 'Levantamiento',
    detalle: 'Procesos, puestos y tareas',
    estado: (m) => (m.progreso.tareas_validadas > 0 && m.progreso.tareas_propuestas === 0 ? 'completo' : m.progreso.tareas_total > 0 ? 'en_curso' : 'pendiente'),
  },
  {
    ruta: 'matriz',
    numero: '3',
    titulo: 'Matriz asistida',
    detalle: 'Peligros, evaluación y medidas',
    estado: (m) => (m.progreso.filas_validadas > 0 && m.progreso.filas_propuestas === 0 && m.progreso.tareas_sin_filas === 0 ? 'completo' : m.progreso.filas_total > 0 ? 'en_curso' : 'pendiente'),
  },
  {
    ruta: 'oficializar',
    numero: '4',
    titulo: 'Oficializar',
    detalle: 'Confirmar y exportar la MIPER',
    estado: (m) => (m.estado === 'oficial' ? 'completo' : m.bloqueos_de_confirmacion.length === 0 ? 'en_curso' : 'pendiente'),
  },
]

export const ETIQUETA_DE_ESTADO_DE_ETAPA: Record<EstadoDeEtapa, string> = {
  completo: 'Completada',
  en_curso: 'En curso',
  pendiente: 'Pendiente',
}

/** Ayuda «Qué hacer aquí»: qué se hace en la vista y qué botón la cierra. */
export const GUIA_DE_VISTA: Record<EtapaDeLaMatriz['ruta'] | 'panel' | 'plan-medidas', { titulo: string; que: string; cierre: string; completada: string }> = {
  panel: {
    titulo: 'Vista general',
    que: 'Revisa el avance de la matriz: tareas validadas, cobertura de peligros, filas por revisar y bloqueos para oficializar.',
    cierre: 'Usa «Continuar revisión» para ir directo a la etapa con trabajo pendiente.',
    completada: 'La matriz está oficializada. Puedes revisar la revisión vigente o descargarla en la etapa 4.',
  },
  alcance: {
    titulo: 'Alcance',
    que: 'Datos de la entidad empleadora, del centro de trabajo, el alcance de la MIPER y quiénes participaron en su construcción.',
    cierre: 'Cambia lo necesario y pulsa «Guardar alcance». El logo es opcional.',
    completada: 'El alcance está registrado. Solo vuelve aquí si cambió algún dato.',
  },
  levantamiento: {
    titulo: 'Levantamiento de procesos',
    que: 'Describe cómo trabaja el área (proceso, área, puesto y una actividad por línea). Atalaya propone las tareas y tú das visto bueno, editas o descartas cada una.',
    cierre: 'Cuando haya al menos una tarea con visto bueno, pulsa «Ir a la matriz asistida».',
    completada: 'Todas las tareas están revisadas. Continúa con la matriz asistida.',
  },
  matriz: {
    titulo: 'Matriz asistida',
    que: 'Por cada tarea validada, Atalaya propone el peligro, el riesgo del catálogo ISP, la evaluación y las medidas con prelación. Revisa fila por fila: visto bueno, editar o descartar.',
    cierre: 'Cuando no queden filas por revisar ni tareas sin peligros, pulsa «Ir a oficializar».',
    completada: 'Todas las filas están revisadas. Continúa a oficializar.',
  },
  oficializar: {
    titulo: 'Oficializar y exportar',
    que: 'Confirma la matriz como oficial con tu nombre y la declaración de revisión. Después revisa la vista previa y descarga el Excel en formato ISP.',
    cierre: 'Pulsa «Confirmar matriz como oficial» y luego «Descargar MIPER oficial (.xlsx)».',
    completada: 'La revisión está oficializada. Descarga el Excel o vuelve a cualquier etapa: un cambio abre el borrador de la revisión siguiente.',
  },
  'plan-medidas': {
    titulo: 'Plan de medidas',
    que: 'Reúne las medidas de la última matriz oficial y las ordena por nivel de riesgo.',
    cierre: 'Descarga el plan y define con tu equipo responsable, fecha y seguimiento.',
    completada: 'El plan queda disponible mientras exista una revisión oficial.',
  },
}

/** Abre la matriz en la etapa donde hay trabajo pendiente: fluidez primero. */
export function rutaSugerida(m: Matriz): string {
  if (m.estado === 'oficial') return 'oficializar'
  if (m.progreso.tareas_total === 0 || m.progreso.tareas_propuestas > 0) return 'levantamiento'
  if (m.progreso.filas_total === 0 || m.progreso.filas_propuestas > 0 || m.progreso.tareas_sin_filas > 0) return 'matriz'
  return 'oficializar'
}
